import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  DollarSign, 
  Calendar, 
  Download, 
  Eye, 
  TrendingUp,
  ArrowLeft,
  FileText,
  CreditCard,
  Clock,
  CheckCircle
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { checkSalaryData } from '../../utils/refreshUserProfile';
import toast from 'react-hot-toast';

const TeacherSalary = () => {
  const { currentUser, userData, userProfile } = useRealtime();
  const [salaryData, setSalaryData] = useState({
    currentSalary: 0,
    currency: 'INR',
    paymentSchedule: 'monthly',
    lastPayment: null,
    nextPayment: null,
    totalEarnings: 0
  });
  const [paymentHistory, setPaymentHistory] = useState([]);

  useEffect(() => {
    console.log('TeacherSalary - User Profile:', userProfile);
    console.log('TeacherSalary - Teacher Profile:', userProfile?.teacherProfile);
    console.log('TeacherSalary - Salary Data:', userProfile?.teacherProfile?.salary);
    
    // Load teacher's salary data
    if (userProfile?.teacherProfile?.salary) {
      const salary = userProfile.teacherProfile.salary;
      console.log('TeacherSalary - Processing salary data:', salary);
      
      const processedSalaryData = {
        currentSalary: salary.baseSalary || 0,
        currency: salary.currency || 'INR',
        paymentSchedule: salary.paymentSchedule || 'monthly',
        lastPayment: salary.lastPayment || null,
        nextPayment: salary.nextPayment || null,
        totalEarnings: salary.totalEarnings || 0,
        paymentDate: salary.paymentDate || null,
        paymentMethod: salary.paymentMethod || 'Not Set',
        breakdown: salary.breakdown || null,
        // Include all the new fields
        baseSalary: salary.baseSalary || 0,
        ratePerClass: salary.ratePerClass || 0,
        bonuses: salary.bonuses || 0,
        deductions: salary.deductions || 0,
        effectiveFrom: salary.effectiveFrom || null,
        bankDetails: salary.bankDetails || null
      };
      
      console.log('TeacherSalary - Processed salary data:', processedSalaryData);
      setSalaryData(processedSalaryData);
    } else {
      console.log('TeacherSalary - No salary data found in user profile');
    }
    
    // Load real payment history from Firestore
    loadPaymentHistory();
  }, [userProfile]);

  const loadPaymentHistory = async () => {
    try {
      // Load real payment history from Firestore
      // This would be implemented based on your payments collection structure
      setPaymentHistory([]);
    } catch (error) {
      console.error('Error loading payment history:', error);
      setPaymentHistory([]);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid':
        return <CheckCircle className="w-4 h-4" />;
      case 'pending':
        return <Clock className="w-4 h-4" />;
      case 'processing':
        return <CreditCard className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: salaryData.currency || 'INR'
    }).format(amount);
  };

  const calculateNetSalary = () => {
    const baseSalary = salaryData.baseSalary || salaryData.currentSalary || 0;
    const bonuses = salaryData.bonuses || 0;
    const deductions = salaryData.deductions || 0;
    return baseSalary + bonuses - deductions;
  };

  const handleRefreshSalaryData = async () => {
    if (!currentUser?.uid) {
      toast.error('User not authenticated');
      return;
    }

    try {
      const salaryData = await checkSalaryData(currentUser.uid);
      if (salaryData) {
        toast.success('Salary data found! Please refresh the page to see updates.');
      } else {
        toast.error('No salary data found. Please contact your admin.');
      }
    } catch (error) {
      toast.error('Error checking salary data');
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center mb-4">
            <Link
              to="/teacher/dashboard"
              className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back to Dashboard
            </Link>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Salary Details</h1>
              <p className="text-gray-600 dark:text-gray-400">View your salary information and payment history</p>
            </div>
            <button
              onClick={handleRefreshSalaryData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Check Salary Data
            </button>
          </div>
        </div>

        {/* Salary Overview */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Monthly Salary (Net)</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {calculateNetSalary() > 0 ? formatCurrency(calculateNetSalary()) : 'Not Set'}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Annual Salary (Net)</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {calculateNetSalary() > 0 ? formatCurrency(calculateNetSalary() * 12) : 'Not Set'}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Last Payment</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {salaryData.lastPayment ? formatCurrency(salaryData.lastPayment.amount) : 'No Payment'}
                </p>
                <p className="text-xs text-gray-500">
                  {salaryData.lastPayment ? new Date(salaryData.lastPayment.date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Next Payment</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {salaryData.nextPayment ? formatCurrency(salaryData.nextPayment.amount) : 'Not Scheduled'}
                </p>
                <p className="text-xs text-gray-500">
                  {salaryData.nextPayment ? new Date(salaryData.nextPayment.date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </motion.div>
        </div>

        {/* Payment Schedule */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Payment Information</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payment Frequency</p>
                <p className="font-medium text-gray-900 dark:text-white capitalize">
                  {salaryData.paymentSchedule || 'Monthly'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Effective From</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {salaryData.effectiveFrom ? new Date(salaryData.effectiveFrom).toLocaleDateString() : 'Not Set'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Payment Method</p>
                <p className="font-medium text-gray-900 dark:text-white capitalize">
                  {salaryData.paymentMethod?.replace('_', ' ') || 'Not Set'}
                </p>
              </div>
            </div>
            
            {/* Bank Details */}
            {salaryData.bankDetails && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                <h3 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Account Holder</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {salaryData.bankDetails.accountHolderName || 'Not Set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Bank Name</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {salaryData.bankDetails.bankName || 'Not Set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Account Number</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {salaryData.bankDetails.accountNumber ? 
                        `****${salaryData.bankDetails.accountNumber.slice(-4)}` : 'Not Set'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">IFSC Code</p>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {salaryData.bankDetails.ifscCode || 'Not Set'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Payment History */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Payment History</h2>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                <Download className="w-4 h-4 inline mr-2" />
                Download All Payslips
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Month</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Amount</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Status</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Paid Date</th>
                    <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentHistory.length > 0 ? (
                    paymentHistory.map((payment) => (
                      <motion.tr
                        key={payment.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="font-medium text-gray-900 dark:text-white">
                              {payment.month}
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-semibold text-gray-900 dark:text-white">
                            {formatCurrency(payment.amount)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(payment.status)}`}>
                            {getStatusIcon(payment.status)}
                            <span className="ml-1 capitalize">{payment.status}</span>
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-gray-600 dark:text-gray-400">
                            {payment.paidDate ? new Date(payment.paidDate).toLocaleDateString() : '-'}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div className="flex space-x-2">
                            {payment.payslipUrl && (
                              <>
                                <button className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg">
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button className="p-2 text-green-600 hover:bg-green-50 rounded-lg">
                                  <Download className="w-4 h-4" />
                                </button>
                              </>
                            )}
                            {payment.status === 'pending' && (
                              <span className="text-sm text-gray-500 px-2 py-1">
                                Processing...
                              </span>
                            )}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="py-12 text-center">
                        <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Payment History</h3>
                        <p className="text-gray-600 dark:text-gray-400">Your payment history will appear here once payments are processed</p>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Salary Breakdown */}
        {salaryData.baseSalary > 0 ? (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Salary Breakdown</h2>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">Earnings</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Base Salary</span>
                      <span className="font-medium">{formatCurrency(salaryData.baseSalary)}</span>
                    </div>
                    {salaryData.ratePerClass > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Rate per Class</span>
                        <span className="font-medium">{formatCurrency(salaryData.ratePerClass)}</span>
                      </div>
                    )}
                    {salaryData.bonuses > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Bonuses</span>
                        <span className="font-medium">{formatCurrency(salaryData.bonuses)}</span>
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex justify-between font-semibold">
                        <span>Gross Salary</span>
                        <span>{formatCurrency((salaryData.baseSalary || 0) + (salaryData.bonuses || 0))}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="font-medium text-gray-900 dark:text-white mb-4">Deductions</h3>
                  <div className="space-y-3">
                    {salaryData.deductions > 0 ? (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">Deductions</span>
                        <span className="font-medium">-{formatCurrency(salaryData.deductions)}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">No deductions</span>
                        <span className="font-medium">₹0</span>
                      </div>
                    )}
                    <div className="border-t pt-3">
                      <div className="flex justify-between font-semibold text-green-600">
                        <span>Net Salary</span>
                        <span>{formatCurrency((salaryData.baseSalary || 0) + (salaryData.bonuses || 0) - (salaryData.deductions || 0))}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Salary Breakdown</h2>
            </div>
            <div className="p-6">
              <div className="text-center py-12">
                <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No Salary Information Available</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  Your salary information will appear here once it's configured by your coaching admin.
                </p>
                
                {/* Debug Information */}
                <div className="text-left bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-w-md mx-auto">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Debug Information:</h4>
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                    <p>User Role: {userProfile?.role || 'Not loaded'}</p>
                    <p>Coaching ID: {userProfile?.coachingId || 'Not set'}</p>
                    <p>Teacher Profile: {userProfile?.teacherProfile ? 'Exists' : 'Missing'}</p>
                    <p>Salary Data: {userProfile?.teacherProfile?.salary ? 'Exists' : 'Missing'}</p>
                    {userProfile?.teacherProfile?.salary && (
                      <p>Base Salary: ₹{userProfile.teacherProfile.salary.baseSalary || 0}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
};

export default TeacherSalary;